import { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { operatorFacingMessage } from '@/lib/utils/errorUtils';
import {
  COMBINE_REMOVE_ORDER,
  SALES_ORDER_COMBINE,
  SALES_ORDER_COMBINE_CONTEXT,
  combineAlertIds,
  type CombineContext
} from '@/lib/combineShipment';

/**
 * While anything about the combined shipment can still change under the operator — another order
 * of the client is being picked (the alert), or a combined order has not reached Verify / Ship yet
 * — the context is re-read this often, so the alert appears when the client's next order is
 * released and the shipment rows move on as the other orders are picked and verified.
 */
export const COMBINE_POLL_MS = 20000;

export type CombineActionResult =
  | {
      ok: true;
      combinedOrderNumber?: string | null;
      bundleNumber?: string | null;
      alreadyCombined?: boolean | null;
    }
  | { ok: false; message: string };

const needsPolling = (context: CombineContext | null) => {
  if (!context) return false;
  if ((context.pickingOrders || []).length) return true;
  return (context.shipment?.orders || []).some((o) => !o.isCurrent && o.stage !== 'ready');
};

/**
 * The combine step's data for one order: the context read (network-only, from the primary), a
 * re-read while it can still change, and the two writes — combine (a scanned / typed code, or an
 * order chosen from the alert) and remove. Every write answers with the fresh context.
 *
 * `enabled` false: nothing is read (e.g. the Pick screen before the pick path is finished). A read
 * that fails is silent — the combine step is an aid, never a reason to stop picking or verifying.
 */
export function useCombineShipment({
  companyId,
  salesOrderId,
  enabled = true
}: {
  companyId: string;
  salesOrderId: string;
  enabled?: boolean;
}) {
  const client = useApolloClient();
  const [context, setContext] = useState<CombineContext | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState<null | 'combine' | 'remove'>(null);
  const busyRef = useRef(busy);
  busyRef.current = busy;
  // Newest-request-wins, as on the Pick screen: a poll that started before a combine must not roll
  // the screen back to the alert the combine just cleared.
  const requestSeqRef = useRef(0);
  const appliedSeqRef = useRef(0);
  const apply = useCallback((seq: number, next: CombineContext | null | undefined) => {
    if (!next || seq < appliedSeqRef.current) return;
    appliedSeqRef.current = seq;
    setContext(next);
  }, []);

  const refresh = useCallback(async () => {
    if (!companyId || !salesOrderId) return null;
    const seq = ++requestSeqRef.current;
    try {
      const { data } = await client.query({
        query: SALES_ORDER_COMBINE_CONTEXT,
        variables: { companyId, salesOrderId },
        fetchPolicy: 'network-only'
      });
      const next = data?.salesOrderCombineContext ?? null;
      apply(seq, next);
      return next as CombineContext | null;
    } catch (e) {
      console.error('salesOrderCombineContext failed:', e);
      return null;
    } finally {
      setLoaded(true);
    }
  }, [apply, client, companyId, salesOrderId]);

  useEffect(() => {
    if (!enabled) return;
    void refresh();
  }, [enabled, refresh]);

  const polling = enabled && needsPolling(context);
  useEffect(() => {
    if (!polling) return undefined;
    const timer = setInterval(() => {
      if (!busyRef.current) void refresh();
    }, COMBINE_POLL_MS);
    return () => clearInterval(timer);
  }, [polling, refresh]);

  const combine = useCallback(
    async (target: { code?: string | null; orderId?: string | null }): Promise<CombineActionResult> => {
      if (busyRef.current) return { ok: false, message: 'Still saving — try again in a moment.' };
      setBusy('combine');
      const seq = ++requestSeqRef.current;
      try {
        const { data } = await client.mutate({
          mutation: SALES_ORDER_COMBINE,
          variables: {
            companyId,
            salesOrderId,
            code: target.code || null,
            combineSalesOrderId: target.orderId || null
          }
        });
        const result = data?.salesOrderCombine;
        apply(seq, result?.context);
        return {
          ok: true,
          combinedOrderNumber: result?.combinedOrderNumber,
          bundleNumber: result?.bundleNumber,
          alreadyCombined: result?.alreadyCombined
        };
      } catch (e) {
        return {
          ok: false,
          message: operatorFacingMessage(e, 'Could not combine the orders. Please try again.')
        };
      } finally {
        setBusy(null);
      }
    },
    [apply, client, companyId, salesOrderId]
  );

  const remove = useCallback(
    async (orderId: string): Promise<CombineActionResult> => {
      if (busyRef.current) return { ok: false, message: 'Still saving — try again in a moment.' };
      setBusy('remove');
      try {
        await client.mutate({
          mutation: COMBINE_REMOVE_ORDER,
          variables: { companyId, salesOrderId: orderId }
        });
        await refresh();
        return { ok: true };
      } catch (e) {
        return {
          ok: false,
          message: operatorFacingMessage(e, 'Could not remove the order from the shipment.')
        };
      } finally {
        setBusy(null);
      }
    },
    [client, companyId, refresh]
  );

  return {
    context,
    /** The first read has answered (or failed) — the alert may now be judged. */
    loaded,
    busy,
    refresh,
    combine,
    remove,
    alertIds: combineAlertIds(context)
  };
}

export default useCombineShipment;
